import React, { useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { Row, Col } from 'react-bootstrap'
import { Loading } from '/src/components/loading'
import { Alert, AddressInput, TextInput, Button } from '/src/components/common'
import { useAddress, useDebounce } from '/src/hooks'
import { cartState } from '/src/hooks/useCart'
import { getRequest, postRequest } from '/src/httpClient'
import { AddressElement } from './AddressElement'
import { formatOptions, OptionLabel } from './util'

const hasAddresses = (values) => {
  return values.length > 0
}

const formatAddressString = (address) => {
  const { address_line1, administrative_area, locality, postal_code } = address
  return `${address_line1} ${administrative_area} ${locality}, ${postal_code}`
}

const AddressElementContainer = ({ cart, user, props }) => {
  const { shipping_information } = cart.attributes
  const [displayForm, setDisplayForm] = useState(false)
  const [addressOptions, setAddressOptions] = useState(false)
  const addresses = useAddress()

  useEffect(() => {
    if (!addressOptions) return
    if (!cart.attributes.shipping_information) return

    const targetAddress = formatAddressString(cart.attributes.shipping_information.address)
    const defaultAddress = addressOptions.find((address) => {
      return formatAddressString(address.address) === targetAddress
    })

    props.setValue('select_address', defaultAddress)
  }, [addressOptions])

  useEffect(() => {
    if (addresses && hasAddresses(addresses)) {
      setAddressOptions(formatOptions(addresses))
    }
    else if (shipping_information && shipping_information.address && shipping_information.address.address_line1) {
      setAddressOptions([{
        label: OptionLabel(shipping_information.address),
        value: 0,
        address: shipping_information.address
      }])
    }

    if (addresses && !hasAddresses(addresses)) {
      if (shipping_information && shipping_information.address && shipping_information.address.address_line1) {
        props.setValue('select_address', addressOptions[0])
      }
      else {
        setDisplayForm(true)
      }
    }
  }, [addresses])

  return (
    <>
      {displayForm
        ? 
          <CustomerAddressFormElement
            setDisplayForm={setDisplayForm}
            setAddressOptions={setAddressOptions}
            defaultValue={shipping_information ? shipping_information.address : null} 
            cart={cart} 
            {...props} />
        : 
          <>
            {addressOptions && (
              <AddressElement 
                options={addressOptions} 
                {...props} />
            )}
            {user && !user.trade && (
              <div className="add-address-button-container mb-4">
                <button onClick={() => setDisplayForm(!displayForm)} className="add-address-button button">
                  Add Delivery Address
                </button>
              </div>
            )}
          </>
      }
    </>
  )
}

const AccountCustomerAddressFormElement = ({ setDisplayForm, setAddressOptions, defaultValue, cart, register, getValues, setValue }) => {
  const [message, setMessage] = useState(false)
  const [addressInput, setAddressInput] = useState('')
  const [options, setOptions] = useState([])
  const [selectedAddress, setSelectedAddress] = useState(false)
  const addressInputDebounce = useDebounce(addressInput, 500)

  useEffect(() => {
    if (!defaultValue || !defaultValue.address_line1) {
      setDisplayForm(true)
      return
    }
    setSelectedAddress(defaultValue)
  }, [defaultValue])

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      if (!addressInputDebounce) {
        return
      }

      const url = '/api/address/location?' + new URLSearchParams({q: addressInputDebounce})
      setOptions(await getRequest(url, controller))
    })()

    return () => controller.abort()
  }, [addressInputDebounce])

  const handleAddressChange = ({ target }) => {
    setAddressInput(target.value)
  }

  const handleAddNewAddress = () => {
    if (!selectedAddress) {
      return
    }
    const [address_line1] = selectedAddress.value.split(',')

    // Create new profile with address.
    postRequest('/api/address/create', {
      data: {
        type: 'profile--customer',
        attributes: {
          address: {
            country_code: 'AU',
            organization: getValues('organization'),
            given_name: getValues('given_name'),
            family_name: getValues('family_name'),
            address_line1: address_line1,
            administrative_area: selectedAddress.administrative_area,
            locality: selectedAddress.locality ? selectedAddress.locality : selectedAddress.city,
            postal_code: selectedAddress.postal_code,
          }
        }
      }
    }).then(values => {
      if (values) {
        setAddressOptions((prev) => {
          const newOption = {
            label: OptionLabel(values.attributes.address),
            value: values.attributes.drupal_internal__profile_id,
            address: values.attributes.address
          }

          setValue('select_address', newOption)
          const results = [newOption]
          if (prev) {
            results.push(...prev)
          }
  
          return results
        })
      }
      else {
        setMessage('Address is not serviceable, please try a different address.')
        console.log('ADDRESS NOT SERVICEABLE')
        setSelectedAddress(false)
      }

      setDisplayForm(false)
    })
  }

  const handleSelectAddress = (option) => {
    setSelectedAddress({
      administrative_area: option.state,
      locality: option.locality,
      city: option.city,
      postal_code: option.postcode,
      address_line1: option.value,
      value: option.value,
      label: option.value
    })
    setOptions([])
  }

  return (
    <div className="add-customer-address-element-container">
      {message && (
        <Row className="mt-4 mb-4">
          <Col xs={12}>
            <Alert>
              <p>{message}</p>
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={6}>
          <TextInput 
            label="First Name"
            defaultValue={selectedAddress ? selectedAddress.given_name : ''}
            register={register('given_name', {
              required: true,
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="Last Name"
            defaultValue={selectedAddress ? selectedAddress.family_name : ''}
            register={register('family_name')} />
        </Col>
        <Col sm={12}>
          <TextInput 
            label="Company"
            defaultValue={selectedAddress ? selectedAddress.organization : ''}
            register={register('organization')} />
        </Col>
        <Col sm={12}>
          <AddressInput
            label="Address"
            register={register('address', {
              onChange: handleAddressChange
            })} />
            {options.length ? (
              <div className="address-options-container">
                <ul>
                  {options.map((option, index) => {
                    return (
                      <li key={index}>
                        <label onClick={() => handleSelectAddress(option, index)}>
                          {option.label}
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : <></>}
        </Col>
        <Col sm={12} className="mt-4">
          <TextInput 
            label="Address"
            value={selectedAddress ? selectedAddress.label : ''}
            register={register('address_disabled', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={12}>
          <TextInput 
            label="Suburb" 
            value={selectedAddress ? selectedAddress.locality : ''}
            register={register('locality', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="State"
            value={selectedAddress ? selectedAddress.administrative_area : ''}
            register={register('administrative_area', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="Postal Code"
            value={selectedAddress ? selectedAddress.postal_code : ''}
            register={register('postal_code', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={12}>
          <div className="actions">
            <Button onClick={() => handleAddNewAddress()}>
              Create
            </Button>
            <Button onClick={() => setDisplayForm(false)}>
              Cancel
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  )
}

const AccountAddressElementContainer = ({ cart, props }) => {
  const [displayForm, setDisplayForm] = useState(false)
  const [addressOptions, setAddressOptions] = useState(false)
  const addresses = useAddress()

  useEffect(() => {
    if (addresses && hasAddresses(addresses)) {
      setAddressOptions(formatOptions(addresses))
    }

    if (addresses && !hasAddresses(addresses)) {
      setDisplayForm(true)
    }
  }, [addresses])

  return (
    <>
      {displayForm
        ? 
          <AccountCustomerAddressFormElement
            setDisplayForm={setDisplayForm}
            setAddressOptions={setAddressOptions}
            cart={cart} 
            {...props} />
        : 
          <>
            {addressOptions && (
              <AddressElement 
                options={addressOptions} 
                {...props} />
            )}
            <div className="add-address-button-container mb-4">
              <button onClick={() => setDisplayForm(!displayForm)} className="add-address-button button">
                Add Delivery Address
              </button>
            </div>
          </>
      }
    </>
  )
}

export const AccountAddressElement = ({ cart, ...props }) => {
  return <AccountAddressElementContainer cart={cart} props={props} />
}

export const CustomerAddressElement = ({ cart, user, ...props }) => {
  const setCart = useSetRecoilState(cartState)
  const [deliverable, setDeliverable] = useState(null)

  props.watch('select_address')
  useEffect(() => {
    // if (displayForm) props.setValue('select_address', 0) 

    const subscription = props.watch((value, { name }) => {
      /*if (name === 'select_address') {
        if (!value.select_address.value || !value.select_address.address) {
          return
        }

        // Check if address is serviceable.
        postRequest('/api/address/deliverable', {
          id: cart.id,
          shipping_information: value.select_address.address
        }).then(response => setDeliverable(response))
      }*/

      if (name === 'equipment') {
        const { select_address, equipment } = value
 
        postRequest('/api/cart/update', {
          id: cart.id,
          type: 'order--default',
          attributes: {
            field_unloading_equipment: String(equipment),
          }
        }).then(values => {
          if (values && select_address && select_address.address) {
            // Perform shipping rate request.
            postRequest('/api/cart/update', {
              id: cart.id,
              type: 'order--default',
              attributes: {
                shipping_information: {
                  address: select_address.address
                }
              }
            }).then(values => {
              if (values) {
                setCart(prev => {
                  return {
                    ...prev,
                    attributes: values.attributes
                  }
                })
              }
            })
          }
        })
      }

      if (name === 'select_address' && value) {
        const { select_address } = value

        if (select_address && select_address.address) {
          // Perform shipping rate request.
          postRequest('/api/cart/update', {
            id: cart.id,
            type: 'order--default',
            attributes: {
              shipping_information: {
                address: select_address.address
              }
            }
          }).then(values => {
            console.log(values)
            if (values) {
              setCart(prev => {
                return {
                  ...prev,
                  attributes: values.attributes
                }
              })
            }
          })
        }
      }
    })
    return () => subscription.unsubscribe()
  }, [props.watch])

  return (
    <>
      {deliverable === false && (
        <div className="form-item error">
          <p>The address selected is not serviceable. Please try changing either selected address or store.</p>
        </div>
      )}
      <AddressElementContainer cart={cart} user={user} props={props} />
      <div className="form-item-radios equipment mb-4">
        <label className="mb-2">Equipment</label>
        <p>Does the delivery address have appropriate unloading equipment? Forklift, Crane, Tractor, etc. 
        Please state the type of equipment and Safe Working Limits in the Delivery Notes below.</p>
        <Row>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
              Yes
                <input type="radio" value="1" {...props.register('equipment')} />
              </label>
            </div>
          </Col>
          <Col sm={6}>
            <div className="form-item radio">
              <label>
                No
                <input type="radio" value="0" {...props.register('equipment')} />
              </label>
            </div>
          </Col>
        </Row>
      </div>
      <DeliveryEstimate user={user} cart={cart} />
    </>
  )
}

const getShippingAdjustmentAmount = ({ adjustments }) => {
  const [result] = adjustments.filter(value => value.type === 'shipping')
  return result ? result.total : false
}

const DeliveryEstimate = ({ user, cart }) => {
  const { order_total } = cart.attributes
  const shipping = getShippingAdjustmentAmount(order_total)

  if (user && !user.trade) {
    if (shipping && shipping.number === '0.00' || shipping && shipping.number === 0) {
      return (
        <div className="delivery-estimate-container mb-4">
          <h3>Selected address not deliverable</h3>
        </div>
      )
    }
  }

  return (
    <div className="delivery-estimate-container mb-4">
      <h3>Delivery: {shipping ? shipping.formatted : '...'}</h3>
    </div>
  )
}

// @todo, move debounce hook inside smaller inner component.
const CustomerAddressFormElement = ({ setDisplayForm, setAddressOptions, defaultValue, cart, register, getValues, setValue }) => {
  const [message, setMessage] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [addressInput, setAddressInput] = useState('')
  const [options, setOptions] = useState([])
  const [selectedAddress, setSelectedAddress] = useState(false)
  const addressInputDebounce = useDebounce(addressInput, 500)

  useEffect(() => {
    if (!defaultValue || !defaultValue.address_line1) {
      setDisplayForm(true)
      return
    }
    setSelectedAddress(defaultValue)
    /*setSelectedAddress({
      ...defaultValue,
      label: 
    })*/
  }, [defaultValue])

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      if (!addressInputDebounce) {
        return
      }

      const url = '/api/address/location?' + new URLSearchParams({q: addressInputDebounce})
      setOptions(await getRequest(url, controller))
    })()

    return () => controller.abort()
  }, [addressInputDebounce])

  const handleAddressChange = ({ target }) => {
    setAddressInput(target.value)
  }

  const handleAddNewAddress = () => {
    if (!selectedAddress) {
      return
    }
    const [address_line1] = selectedAddress.value.split(',')

    console.log(getValues('given_name'))
    console.log(getValues('family_name'))

    if (!getValues('given_name')) {
      setMessage('Please provide a given and family name.')
      return
    }

    if (!getValues('family_name')) {
      setMessage('Please provide a given and family name.')
      return
    }

    // Check if address is serviceable.
    setIsLoading(true)
    postRequest('/api/address/create', {
      data: {
        type: 'profile--customer',
        attributes: {
          address: {
            country_code: 'AU',
            administrative_area: selectedAddress.administrative_area,
            locality: selectedAddress.locality ? selectedAddress.locality : selectedAddress.city,
            postal_code: selectedAddress.postal_code,
            address_line1: address_line1,
            organization: getValues('organization'),
            given_name: getValues('given_name'),
            family_name: getValues('family_name')
          }
        }
      }
      /*id: cart.id,
      shipping_information: {
        address: {
          country_code: 'AU',
          administrative_area: selectedAddress.administrative_area,
          locality: selectedAddress.locality,
          postal_code: selectedAddress.postal_code,
          address_line1: address_line1,
          organization: getValues('organization'),
          given_name: getValues('given_name'),
          family_name: getValues('family_name')
        }
      }*/
    }).then(values => {
      if (!values) {
        // @todo, if address is not serviceable select from stores list which have stock and 
        // are serviceable to that address.
        setMessage('Address is not serviceable, please try a different address.')
        console.log('ADDRESS NOT SERVICEABLE')
        setSelectedAddress(false)
      }
      else {
        const newOption = {
          label: OptionLabel(values.attributes.address),
          value: values.attributes.drupal_internal__profile_id,
          address: values.attributes.address
        }
        setAddressOptions((prev) => {
          const results = [newOption]
          if (prev) {
            results.push(...prev)
          }
  
          return results
        })
        setValue('select_address', newOption)
        setSelectedAddress(newOption.address)
      }

      setIsLoading(false)
      setDisplayForm(false)
    })

    // Update order.
    /*postRequest('/api/cart/update', {
      id: cart.id,
      type: 'order--default',
      attributes: {
        shipping_method: '4--delivery',
        // shipping_method: '1--default',
        shipping_information: {
          address: {
            country_code: 'AU',
            administrative_area: selectedAddress.administrative_area,
            locality: selectedAddress.locality,
            postal_code: selectedAddress.postal_code,
            address_line1: address_line1,
            organization: getValues('organization'),
            given_name: getValues('given_name'),
            family_name: getValues('family_name')
          }
        }
      }
    }).then(values => {
      console.log(values)
      setDisplayForm(false)
    }).catch(values => {
      console.log(values)
    })*/
  }

  const handleSelectAddress = (option) => {
    setSelectedAddress({
      administrative_area: option.state,
      locality: option.locality,
      city: option.city,
      postal_code: option.postcode,
      address_line1: option.value,
      value: option.value,
      label: option.value
    })
    setOptions([])
  }

  return (
    <div className="add-customer-address-element-container">
      {message && (
        <Row className="mt-4 mb-4">
          <Col xs={12}>
            <Alert>
              <p>{message}</p>
            </Alert>
          </Col>
        </Row>
      )}
      <Row>
        <Col sm={6}>
          <TextInput 
            label="First Name"
            defaultValue={selectedAddress ? selectedAddress.given_name : ''}
            register={register('given_name', {
              required: true,
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="Last Name"
            defaultValue={selectedAddress ? selectedAddress.family_name : ''}
            register={register('family_name')} />
        </Col>
        <Col sm={12}>
          <TextInput 
            label="Company"
            defaultValue={selectedAddress ? selectedAddress.organization : ''}
            register={register('organization')} />
        </Col>
        <Col sm={12}>
          <AddressInput
            label="Address"
            register={register('address', {
              onChange: handleAddressChange
            })} />
            {options.length ? (
              <div className="address-options-container">
                <ul>
                  {options.map((option, index) => {
                    return (
                      <li key={index}>
                        <label onClick={() => handleSelectAddress(option, index)}>
                          {option.label}
                        </label>
                      </li>
                    )
                  })}
                </ul>
              </div>
            ) : <></>}
        </Col>
        <Col sm={12}>
          <TextInput 
            label="Address"
            value={selectedAddress ? selectedAddress.label : ''}
            register={register('address_disabled', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={12}>
          <TextInput 
            label="Suburb" 
            value={selectedAddress ? selectedAddress.locality : ''}
            register={register('locality', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="State"
            value={selectedAddress ? selectedAddress.administrative_area : ''}
            register={register('administrative_area', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={6}>
          <TextInput 
            label="Postal Code"
            value={selectedAddress ? selectedAddress.postal_code : ''}
            register={register('postal_code', {
              required: true,
              disabled: true
            })} />
        </Col>
        <Col sm={12}>
          <div className="actions">
            <Button disabled={isLoading} onClick={() => handleAddNewAddress()}>
              Set Delivery Address
              {isLoading && (
                <Loading />
              )}
            </Button>
            <Button disabled={isLoading} onClick={() => setDisplayForm(false)}>
              Cancel
            </Button>
          </div>
        </Col>
      </Row>
    </div>
  )
}
